import React, { useMemo } from 'react'
import { ReportTimeTrack } from 'Types/Reports/ReportTimeTrack'
import styles from './styles.module.scss'
import { Table } from 'reactstrap'

interface IProps {
  timeTrackReports: ReportTimeTrack[]
}

const formatHours = (hours: number) =>
  Number(hours).toLocaleString('en-US', { maximumFractionDigits: 2 })

const ReportsTimeTrack: React.FC<IProps> = (props: IProps) => {
  const totals = useMemo(() => {
    const reports = props.timeTrackReports
    const spent = reports.reduce((sum, report) => sum + Number(report.workingHours), 0)
    const chargeable = reports.reduce((sum, report) => sum + Number(report.chargeableHours), 0)
    return {
      spent,
      chargeable,
      chargeablePercent: spent > 0 ? Math.round((chargeable / spent) * 100) : 0,
      employees: new Set(reports.map((report) => report.userFullName)).size,
      projects: new Set(reports.map((report) => report.projectName)).size,
    }
  }, [props.timeTrackReports])

  return (
    <>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Spent hours</span>
          <span className={styles.statValue}>{formatHours(totals.spent)}h</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Chargeable hours</span>
          <span className={styles.statValue}>
            {formatHours(totals.chargeable)}h
            <span className={styles.statHint}>{totals.chargeablePercent}%</span>
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Employees</span>
          <span className={styles.statValue}>{totals.employees}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Projects</span>
          <span className={styles.statValue}>{totals.projects}</span>
        </div>
      </div>

      <Table className={styles.table} responsive>
        <thead>
          <tr>
            <th>Employee</th>
            <th className={styles.optionalColumn}>Client</th>
            <th>Project</th>
            <th className={styles.numeric}>Spent hours</th>
            <th className={styles.numeric}>Chargeable hours</th>
          </tr>
        </thead>
        <tbody>
          {props.timeTrackReports.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-muted py-4">
                No time tracked for the selected filters.
              </td>
            </tr>
          )}
          {props.timeTrackReports.map((report, i) => (
            <tr key={i}>
              <td>{report.userFullName}</td>
              <td className={styles.optionalColumn}>{report.clientName}</td>
              <td>{report.projectName}</td>
              <td className={styles.numeric}>{formatHours(report.workingHours)}</td>
              <td className={styles.numeric}>{formatHours(report.chargeableHours)}</td>
            </tr>
          ))}
        </tbody>
        {props.timeTrackReports.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={2}>Total</td>
              <td className={styles.optionalColumn} />
              <td className={styles.numeric}>{formatHours(totals.spent)}</td>
              <td className={styles.numeric}>{formatHours(totals.chargeable)}</td>
            </tr>
          </tfoot>
        )}
      </Table>
    </>
  )
}

export default ReportsTimeTrack
